import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
    BellIcon,
    CirclesDeco,
    LockIcon,
    MessageIcon,
    RadarChart,
    RadialProgress,
} from "../../assets/icons/icons";

import teamTrustLogo from "../../assets/images/team-trust-logo.png";
import Field from "../../components/common/Field";
import useOrganization from "../organization/hooks/useOrganization";
import { forgotPassword } from "./api/auth.api";

const ForgotPassword: React.FC = () => {
    document.title = `Forgot Password - ${process.env.REACT_APP_NAME}`;

    const { currentOrganization } = useOrganization();
    const [submitted, setSubmitted] = useState(false);

    const formik = useFormik({
        initialValues: { email: "" },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await forgotPassword(values.email);

                setSubmitted(true);
            } catch (error: any) {
                console.error("Forgot password error:", error);

                alert(
                    error?.response?.data?.message || "Something went wrong"
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div className="login-page">
            {/* LEFT SIDE */}
            <div className="login-left">
                <header className="create-account-header">
                    <img
                        src={currentOrganization?.image ?? teamTrustLogo}
                        alt="logo"
                        className="login-logo"
                    />
                </header>

                <div className="login-body">
                    <div className="login-card max-w-[444px]">
                        <h1
                            className={`login-title w-full ${submitted ? "text-center" : ""
                                }`}
                        >
                            Forgot Password
                        </h1>

                        {!submitted ? (
                            <>
                                <p className="login-description">
                                    Enter your email and we’ll send you a reset link.
                                </p>

                                <div className="max-w-[315px] w-full">
                                    <form
                                        onSubmit={formik.handleSubmit}
                                        className="flex flex-col gap-5"
                                    >
                                        <Field label="Email">
                                            <input
                                                id="email"
                                                type="email"
                                                placeholder="Email Address"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="login-input"
                                            />
                                            {formik.touched.email && formik.errors.email && (
                                                <span className="form-error">
                                                    {formik.errors.email}
                                                </span>
                                            )}
                                        </Field>

                                        <button type="submit" className="login-submit w-full">
                                            Send Reset Link
                                        </button>
                                    </form>

                                    {/* ✅ FIXED ALIGNMENT */}
                                    <div className="mt-4 text-center w-full">
                                        <Link
                                            to="/login"
                                            className="login-forgot hover:underline"
                                        >
                                            Back to Login
                                        </Link>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center text-center mt-6 space-y-4 max-w-[315px] mx-auto">

                                {/* ICON */}
                                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100">
                                    <span className="text-green-600 text-xl">✔</span>
                                </div>

                                {/* MESSAGE */}
                                <p className="text-lg font-semibold text-dark-blue">
                                    Reset link sent!
                                </p>

                                <p className="text-sm text-gray-500">
                                    We've sent a password reset link to your email. Please check your inbox.
                                </p>

                                {/* BUTTON */}
                                <Link
                                    to="/login"
                                    className="login-submit w-full text-center mt-2"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        )}

                        <div className="login-footer-links">
                            <ul className="login-footer-list">
                                <li>
                                    <Link to="/privacy-policy">Privacy Policy</Link>
                                </li>
                                <li className="mx-2.5 w-1.5 h-1.5 rounded-full bg-[#7B9DED]" />
                                <li>
                                    <Link to="/communication-usage-terms">
                                        Communication & Usage Terms
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="login-info">
                        <h2 className="login-info-title">
                            Build winning teams,
                            <br />
                            faster and easier.
                        </h2>
                        <p className="login-info-text">Thrive and win together.</p>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE (ICONS FIXED HERE) */}
            <div className="login-right">
                <CirclesDeco />

                <div
                    className="relative mx-auto w-full px-4 py-11"
                    style={{ maxWidth: 786 }}
                >
                    <div
                        className="relative mx-auto"
                        style={{ maxWidth: 525, paddingTop: 77 }}
                    >
                        <div
                            className="absolute flex items-center justify-center rounded-full bg-white z-10"
                            style={{
                                top: 0,
                                left: 147,
                                width: 70,
                                height: 70,
                                fontSize: 70,
                                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
                            }}
                        >
                            <MessageIcon className="text-dark-blue" />
                        </div>

                        <div
                            className="absolute flex items-center justify-center rounded-full bg-white z-10"
                            style={{
                                top: 107,
                                left: -15,
                                width: 101,
                                height: 101,
                                fontSize: 101,
                                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
                            }}
                        >
                            <BellIcon className="text-dark-blue" />
                        </div>

                        <div
                            className="absolute flex items-center justify-center rounded-full bg-white z-10"
                            style={{
                                bottom: 13,
                                right: 59,
                                width: 72,
                                height: 72,
                                fontSize: 72,
                                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
                            }}
                        >
                            <LockIcon className="text-dark-blue" />
                        </div>

                        <div
                            style={{
                                position: "relative",
                                marginLeft: "auto",
                                maxWidth: 272,
                                zIndex: 3,
                                paddingBottom: 49,
                            }}
                        >
                            <RadialProgress />
                        </div>

                        <div
                            className="relative bg-white"
                            style={{
                                marginTop: -92,
                                maxWidth: 301,
                                zIndex: 2,
                                boxShadow: "0 10px 40px 0 rgba(0,0,0,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    paddingTop: `${(233 / 301) * 100}%`,
                                }}
                            >
                                <div className="absolute inset-0 flex items-center justify-center p-3">
                                    <RadarChart />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="relative mx-auto text-center px-4"
                        style={{ marginTop: 77, maxWidth: 400, zIndex: 1 }}
                    >
                        <h2
                            className="font-bold text-dark-blue leading-snug mb-6"
                            style={{ fontSize: 35, fontFamily: "var(--font-primary)" }}
                        >
                            Build winning teams,
                            <br />
                            faster and easier.
                        </h2>
                        <p
                            className="text-dark-blue"
                            style={{ fontSize: 18, fontFamily: "var(--font-primary)" }}
                        >
                            Thrive and win together.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;